import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { green, red } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Badge,
} from "@material-ui/core";
import {
  Edit,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  Refresh,
  Settings,
  Warning,
  NotificationsActive,
  CheckCircle,
} from "@material-ui/icons";
import { Stack } from "@mui/material";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";
import alertSound from "../../assets/chat_notify.mp3";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    borderRadius: "10px",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    ...theme.scrollbarStyles,
  },
  TableHead: {
    backgroundColor: theme.palette.barraSuperior,
    color: "textSecondary",
    borderRadius: "5px",
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: { textAlign: "center" },
  buttonProgress: { color: green[500] },
  alertBanner: {
    margin: "0 20px 16px",
    padding: "16px 20px",
    borderRadius: 12,
    border: "1px solid rgba(211, 47, 47, 0.3)",
    backgroundColor: "rgba(211, 47, 47, 0.06)",
  },
  alertItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 8,
    border: "1px solid rgba(211, 47, 47, 0.15)",
    backgroundColor: theme.palette.type === "dark" ? "rgba(211,47,47,0.08)" : "rgba(211,47,47,0.04)",
  },
  alertItemConnected: {
    border: "1px solid rgba(76, 175, 80, 0.3)",
    backgroundColor: theme.palette.type === "dark" ? "rgba(76,175,80,0.08)" : "rgba(76,175,80,0.04)",
  },
  reconnectBtn: {
    textTransform: "none",
    fontSize: 12,
    minWidth: 100,
  },
  settingsBtn: {
    marginLeft: theme.spacing(1),
  },
  disconnectedRow: {
    backgroundColor: "rgba(211, 47, 47, 0.04)",
  },
}));


const EVENT_LABELS = {
  disconnected: "🔴 Desconectada",
  logged_out: "🚪 Logout do celular",
  session_banned: "⛔ Sessão banida",
  reconnect_failed: "❌ Falha na reconexão",
  connected: "🟢 Reconectada",
};

const AllConnections = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { list } = useCompanies();
  const history = useHistory();
  const audioRef = useRef(null);

  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [whats, setWhats] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);
  const [confirmModalInfo, setConfirmModalInfo] = useState({ action: "", title: "", message: "", whatsAppId: "", open: false });

  // ✅ ALERTAS EM TEMPO REAL
  const [globalAlerts, setGlobalAlerts] = useState([]);
  const [reconnectingIds, setReconnectingIds] = useState(new Set());

  // ✅ CONFIGURAÇÕES DE NOTIFICAÇÃO
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notifSettings, setNotifSettings] = useState({
    notificationWhatsappId: null,
    notificationNumber: "",
    notificationEnabled: false,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  if (!user.super) {
    history.push("/tickets");
  }

  const playAlertSound = useCallback(() => {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
      audioRef.current = new Audio(alertSound);
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch(() => {});
    } catch (e) {}
  }, []);

  // Carregar conexões e empresas
  useEffect(() => {
    const fetchData = async () => {
      setLoadingWhatsapp(true);
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
      } catch (err) { toastError(err); }
      setLoadingWhatsapp(false);
    };
    fetchData();

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
      } catch (err) {}
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const companyList = await list();
        setCompanies(companyList);
      } catch (e) {
        toast.error("Não foi possível carregar a lista de empresas");
      }
    };
    loadCompanies();
  }, [list]);

  // Carregar configurações de notificação
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await api.get("/whatsapp-notification-settings");
        setNotifSettings(data);
      } catch (err) {}
    };
    if (user.super) loadSettings();
  }, [user.super]);

  // ✅ ESCUTAR ALERTAS GLOBAIS VIA SOCKET
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const onGlobalAlert = (data) => {
      if (!data || !data.action) return;

      // Se reconectou, remover alerta
      if (data.action === "connected") {
        setGlobalAlerts((prev) => {
          const filtered = prev.filter((a) => a.whatsappId !== data.whatsappId);
          return filtered;
        });
        // Atualizar lista de conexões
        api.get("/whatsapp/all/?session=0").then(({ data: whatsData }) => setWhats(whatsData)).catch(() => {});
        return;
      }

      // Adicionar alerta
      const newAlert = {
        id: `${data.companyId}-${data.whatsappId}-${Date.now()}`,
        ...data,
        timestamp: data.timestamp || new Date().toISOString(),
      };

      setGlobalAlerts((prev) => {
        const filtered = prev.filter((a) => !(a.whatsappId === data.whatsappId && a.companyId === data.companyId));
        return [newAlert, ...filtered];
      });

      playAlertSound();

      // Atualizar lista de conexões
      api.get("/whatsapp/all/?session=0").then(({ data: whatsData }) => setWhats(whatsData)).catch(() => {});
    };

    socket.on(`company-${user.companyId}-globalConnectionAlert`, onGlobalAlert);

    return () => {
      socket.off(`company-${user.companyId}-globalConnectionAlert`, onGlobalAlert);
    };
  }, [socket, user?.companyId, playAlertSound]);

  // ✅ RECONECTAR REMOTO
  const handleRemoteReconnect = async (whatsappId) => {
    setReconnectingIds((prev) => new Set([...prev, whatsappId]));
    try {
      await api.post(`/whatsapp-remote-reconnect/${whatsappId}`);
      toast.success("Reconectando...");
      // Remover alerta após 3s
      setTimeout(() => {
        setGlobalAlerts((prev) => prev.filter((a) => a.whatsappId !== whatsappId));
        setReconnectingIds((prev) => { const n = new Set(prev); n.delete(whatsappId); return n; });
        api.get("/whatsapp/all/?session=0").then(({ data }) => setWhats(data)).catch(() => {});
      }, 5000);
    } catch (err) {
      toastError(err);
      setReconnectingIds((prev) => { const n = new Set(prev); n.delete(whatsappId); return n; });
    }
  };

  // ✅ SALVAR CONFIGURAÇÕES DE NOTIFICAÇÃO
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/whatsapp-notification-settings", notifSettings);
      setNotifSettings(data);
      toast.success("Configurações de notificação salvas!");
      setSettingsOpen(false);
    } catch (err) {
      toastError(err);
    }
    setSavingSettings(false);
  };

  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, []);

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);


  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try { await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`); } catch (err) { toastError(err); }
    }
    if (confirmModalInfo.action === "delete") {
      try { await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`); toast.success(i18n.t("connections.toasts.deleted")); } catch (err) { toastError(err); }
    }
    setConfirmModalInfo({ action: "", title: "", message: "", whatsAppId: "", open: false });
  };

  const formatTime = (timestamp) => {
    try { return new Date(timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return ""; }
  };

  // Conexões da empresa padrão (companyId=1) para o select de notificações
  const company1Whatsapps = whats.filter((w) => w.companyId === 1 && w.status === "CONNECTED");

  // Contar desconectadas por empresa
  const getDisconnectedCount = (companyId) => whats.filter((w) => w.companyId === companyId && w.status !== "CONNECTED").length;
  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      {/* ✅ MODAL DE CONFIGURAÇÕES DE NOTIFICAÇÃO */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <NotificationsActive style={{ color: "#1976d2" }} />
            Configurações de Notificação
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Configure uma conexão WhatsApp da empresa padrão para enviar alertas automáticos quando conexões de qualquer empresa caírem.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={notifSettings.notificationEnabled}
                onChange={(e) => setNotifSettings({ ...notifSettings, notificationEnabled: e.target.checked })}
                color="primary"
              />
            }
            label="Habilitar notificações via WhatsApp"
            style={{ marginBottom: 16 }}
          />

          <FormControl fullWidth style={{ marginBottom: 16 }}>
            <InputLabel>Conexão para enviar notificações</InputLabel>
            <Select
              value={notifSettings.notificationWhatsappId || ""}
              onChange={(e) => setNotifSettings({ ...notifSettings, notificationWhatsappId: e.target.value || null })}
              label="Conexão para enviar notificações"
            >
              <MenuItem value="">Nenhuma</MenuItem>
              {company1Whatsapps.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name} ({w.number})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Número que recebe as notificações"
            placeholder="5511999999999"
            value={notifSettings.notificationNumber}
            onChange={(e) => setNotifSettings({ ...notifSettings, notificationNumber: e.target.value })}
            helperText="Número com DDD e código do país (ex: 5511999999999)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveSettings} color="primary" variant="contained" disabled={savingSettings}>
            {savingSettings ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <Paper className={classes.mainPaper} style={{ overflow: "hidden" }} variant="outlined">
          <MainHeader>
            <Stack>
              <Typography variant="h5" color="textPrimary" style={{ fontWeight: "bold", marginLeft: "10px", marginTop: "10px" }} gutterBottom>
                {i18n.t("connections.title")}
              </Typography>
              <Typography style={{ marginTop: "-10px", marginLeft: "10px" }} variant="caption" color="textSecondary">
                Monitoramento global de conexões de todas as empresas
              </Typography>
            </Stack>
            <MainHeaderButtonsWrapper>
              <Badge badgeContent={globalAlerts.length} color="error" invisible={globalAlerts.length === 0}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<Warning />}
                  disabled={globalAlerts.length === 0}
                  onClick={() => {
                    const el = document.getElementById("alerts-section");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {globalAlerts.length} Alerta{globalAlerts.length !== 1 ? "s" : ""}
                </Button>
              </Badge>
              <Tooltip title="Configurar notificações WhatsApp">
                <IconButton onClick={() => setSettingsOpen(true)} className={classes.settingsBtn}>
                  <Settings />
                </IconButton>
              </Tooltip>
            </MainHeaderButtonsWrapper>
          </MainHeader>

          <Stack style={{ overflowY: "auto", padding: "20px", backgroundColor: "rgb(244 244 244 / 53%)", borderRadius: "5px", height: "93%" }}>

            {/* ✅ SEÇÃO DE ALERTAS EM TEMPO REAL */}
            {globalAlerts.length > 0 && (
              <div id="alerts-section" className={classes.alertBanner}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Warning style={{ color: "#d32f2f", fontSize: 20 }} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      Alertas de Conexão ({globalAlerts.length})
                    </Typography>
                  </div>
                  <Button size="small" onClick={() => setGlobalAlerts([])} style={{ textTransform: "none", fontSize: 12 }}>
                    Limpar todos
                  </Button>
                </div>

                {globalAlerts.map((alert) => (
                  <div key={alert.id} className={`${classes.alertItem} ${alert.action === "connected" ? classes.alertItemConnected : ""}`}>
                    <div style={{ flex: 1 }}>
                      <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13 }}>
                        {alert.companyName || `Empresa #${alert.companyId}`} — {alert.whatsappName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {EVENT_LABELS[alert.action] || alert.action} • {formatTime(alert.timestamp)}
                        {alert.details && ` • ${alert.details}`}
                      </Typography>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {alert.action !== "connected" && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          className={classes.reconnectBtn}
                          disabled={reconnectingIds.has(alert.whatsappId)}
                          startIcon={reconnectingIds.has(alert.whatsappId) ? <CircularProgress size={14} /> : <Refresh style={{ fontSize: 14 }} />}
                          onClick={() => handleRemoteReconnect(alert.whatsappId)}
                        >
                          {reconnectingIds.has(alert.whatsappId) ? "Reconectando..." : "Reconectar"}
                        </Button>
                      )}
                      {alert.action === "connected" && (
                        <Chip size="small" icon={<CheckCircle style={{ color: green[500], fontSize: 16 }} />} label="Restaurada" style={{ backgroundColor: "rgba(76,175,80,0.1)" }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ✅ TABELA DE EMPRESAS */}
            <Paper>
              <Table size="small">
                <TableHead className={classes.TableHead}>
                  <TableRow>
                    <TableCell style={{ color: "#fff" }} align="center">Empresa</TableCell>
                    <TableCell style={{ color: "#fff" }} align="center">Conectadas</TableCell>
                    <TableCell style={{ color: "#fff" }} align="center">Desconectadas</TableCell>
                    <TableCell style={{ color: "#fff" }} align="center">Total</TableCell>
                    <TableCell style={{ color: "#fff" }} align="center">Status</TableCell>
                    {user.profile === "admin" && (
                      <TableCell style={{ color: "#fff" }} align="center">Ações</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingWhatsapp ? (
                    <TableRowSkeleton />
                  ) : (
                    <>
                      {companies?.length > 0 && companies.map((company) => {
                        const connected = whats.filter((w) => w.companyId === company.id && w.status === "CONNECTED").length;
                        const disconnected = whats.filter((w) => w.companyId === company.id && w.status !== "CONNECTED").length;
                        const total = whats.filter((w) => w.companyId === company.id).length;
                        const hasAlert = globalAlerts.some((a) => a.companyId === company.id);

                        return (
                          <TableRow key={company.id} className={hasAlert ? classes.disconnectedRow : ""}>
                            <TableCell align="center">
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                {hasAlert && <Warning style={{ color: "#d32f2f", fontSize: 16 }} />}
                                {company.name}
                              </div>
                            </TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={connected} style={{ backgroundColor: connected > 0 ? "rgba(76,175,80,0.15)" : "transparent", color: green[700], fontWeight: 600 }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={disconnected} style={{ backgroundColor: disconnected > 0 ? "rgba(211,47,47,0.15)" : "transparent", color: disconnected > 0 ? red[700] : "inherit", fontWeight: 600 }} />
                            </TableCell>
                            <TableCell align="center">{total}</TableCell>
                            <TableCell align="center">
                              {disconnected > 0 ? (
                                <Chip size="small" icon={<SignalCellularConnectedNoInternet0Bar style={{ fontSize: 14, color: red[500] }} />} label="Com problemas" style={{ backgroundColor: "rgba(211,47,47,0.1)", fontSize: 11 }} />
                              ) : total > 0 ? (
                                <Chip size="small" icon={<SignalCellular4Bar style={{ fontSize: 14, color: green[500] }} />} label="Todas OK" style={{ backgroundColor: "rgba(76,175,80,0.1)", fontSize: 11 }} />
                              ) : (
                                <Typography variant="caption" color="textSecondary">Sem conexões</Typography>
                              )}
                            </TableCell>
                            {user.profile === "admin" && (
                              <TableCell align="center">
                                <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                  <Tooltip title="Ver conexões">
                                    <IconButton size="small" onClick={() => handleOpenWhatsAppModal(whats.filter((w) => w.companyId === company.id), company)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {disconnected > 0 && (
                                    <Tooltip title="Reconectar todas desconectadas">
                                      <IconButton size="small" onClick={() => {
                                        const disconnectedWhats = whats.filter((w) => w.companyId === company.id && w.status !== "CONNECTED" && w.status !== "OPENING");
                                        disconnectedWhats.forEach((w) => handleRemoteReconnect(w.id));
                                      }}>
                                        <Refresh fontSize="small" style={{ color: "#1976d2" }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}

                      {/* TOTAIS */}
                      <TableRow className={classes.TableHead}>
                        <TableCell style={{ color: "#fff" }} align="center">Total</TableCell>
                        <TableCell style={{ color: "#fff" }} align="center">
                          {whats.filter((w) => w.status === "CONNECTED").length}
                        </TableCell>
                        <TableCell style={{ color: "#fff" }} align="center">
                          {whats.filter((w) => w.status !== "CONNECTED").length}
                        </TableCell>
                        <TableCell style={{ color: "#fff" }} align="center">{whats.length}</TableCell>
                        <TableCell style={{ color: "#fff" }} align="center" />
                        {user.profile === "admin" && <TableCell style={{ color: "#fff" }} align="center" />}
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        </Paper>
      )}
    </MainContainer>
  );
};

export default AllConnections;
